import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Box, Button, ButtonBase, CircularProgress, Collapse, Link, Typography } from '@mui/material';
import RoundedPaper from 'shared/components/RoundedPaper';
import AgentHubService from 'services/AgentHubService';
import { AgentStatus, fmtTime, PendingCommand, runLine, StatusChip } from './parts';
import AgentSettings from './AgentSettings';
import TaskDialog from './TaskDialog';
import { AgentState, Command, EventRow, EventType, RunRow, parseVariant } from './types';

const service = new AgentHubService();

const EVENT_LOOK: Record<EventType, { label: string; color: string }> = {
  assistant: { label: 'thinks', color: '#334155' },
  tool_call: { label: 'does', color: '#4F46E5' },
  tool_result: { label: 'result', color: '#94A3B8' },
  live_action: { label: 'LIVE', color: '#C2410C' },
  denied: { label: 'refused', color: '#B91C1C' },
  pr_opened: { label: 'PR', color: '#047857' },
  note: { label: 'hub', color: '#64748B' },
  rate_limit: { label: 'limit', color: '#B45309' },
  error: { label: 'error', color: '#B91C1C' },
  report: { label: 'report', color: '#047857' },
  consult: { label: 'asks', color: '#7C3AED' },
  hand_off: { label: 'hands off', color: '#7C3AED' },
  billing_guard: { label: 'BILLING', color: '#B91C1C' },
};

function EventLine({ e }: { e: EventRow }) {
  const [open, setOpen] = useState(false);
  const look = EVENT_LOOK[e.type] ?? { label: e.type, color: '#334155' };
  const detail = parseVariant<unknown>(e.detail, null);
  const lineSx = { display: 'flex', gap: 1, alignItems: 'baseline' };
  const line = (
    <>
      <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 64, fontVariantNumeric: 'tabular-nums' }}>{fmtTime(e.ts).split(', ').pop()}</Typography>
      <Typography variant="caption" sx={{ color: look.color, fontWeight: 700, minWidth: 52 }}>{look.label}</Typography>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word', fontWeight: e.type === 'live_action' ? 700 : 400 }}>{e.summary}</Typography>
    </>
  );
  return (
    <Box sx={{ py: 0.4, borderTop: '1px solid #F1F5F9', opacity: e.type === 'tool_result' ? 0.7 : 1 }}>
      {detail != null
        ? <ButtonBase component="div" aria-expanded={open} onClick={() => setOpen(o => !o)} sx={{ ...lineSx, width: '100%', justifyContent: 'flex-start', textAlign: 'left', '&.Mui-focusVisible': { outline: '2px solid #4F46E5' } }}>{line}</ButtonBase>
        : <Box sx={lineSx}>{line}</Box>}
      {detail != null && <Collapse in={open}><Box component="pre" sx={{ m: 0, ml: '128px', p: 1, bgcolor: '#F8FAFC', fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 360, overflow: 'auto' }}>{typeof detail === 'string' ? detail : JSON.stringify(detail, null, 2)}</Box></Collapse>}
    </Box>
  );
}

const TRIGGER_LABEL: Record<string, string> = { trigger: 'task', run_now: 'run now', schedule: 'scheduled', continuous: 'scheduled', hand_off: 'handed off by another agent', consult: 'asked by another agent' };

/** A run's report as its list of outcomes: markdown links become links; the "## Report" heading is dropped. */
function ReportList({ report }: { report: string }) {
  const lines = report.split('\n').map(l => l.trim()).filter(l => l && !/^#+\s*report\s*$/i.test(l));
  const linked = (text: string) => text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
    const m = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    return m ? <Link key={i} href={m[2]} target="_blank" rel="noreferrer">{m[1]}</Link> : part.replace(/\*\*|`/g, '');
  });
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
      {lines.map((l, i) => {
        const text = l.replace(/^([-*]|\d+[.)])\s+/, '');
        // A short label on its own line (older reports: "Done", "Found", "Next"): a small heading, not a bullet.
        const label = text.replace(/\*\*|[:—-]\s*$/g, '').trim();
        if (label.split(/\s+/).length <= 3 && !/[.\]]/.test(label)) return <Typography key={i} component="li" variant="caption" sx={{ display: 'block', listStyle: 'none', ml: -2.5, mt: i ? 1 : 0, fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>;
        return <Typography key={i} component="li" variant="body2">{linked(text)}</Typography>;
      })}
    </Box>
  );
}

function RunView({ runId }: { runId: string }) {
  const [data, setData] = useState<{ run: RunRow; events: EventRow[]; children: RunRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLog, setShowLog] = useState(false);
  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    service.run(runId).then(d => live && setData(d)).catch(e => live && setError(e.message));
    return () => { live = false; };
  }, [runId]);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  const { run, events, children } = data;
  const prs = parseVariant<string[]>(run.pr_urls, []);
  const live = parseVariant<Array<{ tool: string; input: unknown; result: unknown; at: string }>>(run.live_actions, []);
  const denials = parseVariant<Array<{ tool: string; input: unknown }>>(run.denials, []);
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <StatusChip status={run.status} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {fmtTime(run.started_at)} · {run.minutes ?? '—'} min · {run.mode === 'consult' ? 'answering another agent' : TRIGGER_LABEL[String(run.triggered_by)] ?? run.triggered_by}
        </Typography>
      </Box>
      {run.task && <Typography variant="body2" title={run.task} sx={{ mt: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><b>{run.mode === 'consult' ? 'Question' : 'Task'}:</b> {run.task.split('\n')[0]}</Typography>}
      {run.parent_run_id && <Typography variant="body2" sx={{ mt: 0.5 }}>{run.mode === 'consult' ? 'Consulted' : 'Handed this task'} by run <RouterLink to={`/agents/${run.parent_run_id.split('-20')[0]}?run=${encodeURIComponent(run.parent_run_id)}`}>{run.parent_run_id}</RouterLink></Typography>}
      {children.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2"><b>Asked other agents:</b></Typography>
          {children.map(c => <Typography key={c.run_id} variant="body2" sx={{ ml: 1 }}>{c.mode === 'consult' ? 'consulted' : 'handed off to'} <RouterLink to={`/agents/${c.agent}?run=${encodeURIComponent(c.run_id)}`}>{c.agent}</RouterLink> — {c.summary ?? c.status}</Typography>)}
        </Box>
      )}
      {run.error && <Alert severity="error" sx={{ mt: 1 }}>{run.error}</Alert>}
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>What it did</Typography>
      {run.report ? <ReportList report={run.report} /> : <Typography variant="body2" sx={{ color: 'text.secondary' }}>{run.status === 'running' ? 'Still running.' : 'No report.'}</Typography>}
      {prs.length > 0 && <Typography variant="body2" sx={{ mt: 1 }}><b>Pull requests:</b> {prs.map(u => <Box key={u} component="a" href={u} target="_blank" rel="noreferrer" sx={{ mr: 1.5 }}>{u.replace('https://github.com/shareably/', '')}</Box>)}</Typography>}
      {live.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <b>{live.length} live action{live.length === 1 ? '' : 's'}:</b>
          {live.map((l, i) => <Box key={i} sx={{ fontSize: 13 }}>{fmtTime(l.at)} · <code>{l.tool}</code> {JSON.stringify(l.input)} → {String(l.result).slice(0, 200)}</Box>)}
        </Alert>
      )}
      {denials.length > 0 && <Alert severity="info" sx={{ mt: 1 }}><b>Refused:</b> {denials.length} step{denials.length === 1 ? '' : 's'} (in the full log).</Alert>}
      <Link component="button" variant="caption" onClick={() => setShowLog(v => !v)} sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>{showLog ? 'Hide' : 'Show'} the full log ({events.length} steps)</Link>
      <Collapse in={showLog}>{showLog && events.map(e => <EventLine key={e.seq} e={e} />)}</Collapse>
    </Box>
  );
}

/** One agent: its settings, its runs, and the selected run in full. */
export default function AgentDetail({ name, agent, pending, onCommand, busy, notice, onNotice, commandError, onDismissError }: { name: string; agent: AgentState | null; pending?: PendingCommand; onCommand: (a: string, c: Command) => void; busy: boolean; notice: string | null; onNotice: (m: string) => void; commandError: string | null; onDismissError: () => void }) {
  const [tasking, setTasking] = useState(false);
  const location = useLocation();
  const selected = new URLSearchParams(location.search).get('run');
  const [runs, setRuns] = useState<RunRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setRuns(null);
    setError(null);
    service.runs(name, 60).then(r => live && setRuns(r)).catch(e => live && setError(e.message));
    return () => { live = false; };
  }, [name]);
  const current = selected ?? runs?.[0]?.run_id ?? null;

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="body2"><RouterLink to="/agents">← Agents</RouterLink></Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{name}</Typography>
        {agent && <AgentStatus a={agent} pending={pending} />}
        <Box sx={{ flex: 1 }} />
        {agent && <>
          {agent.canRunNow && <Button size="small" variant="outlined" disabled={busy || !!pending || !!agent.running} onClick={() => onCommand(name, 'run_now')}>Run now</Button>}
          {agent.onDemand && <Button size="small" variant="outlined" disabled={busy} onClick={() => setTasking(true)}>Give a task</Button>}
          <Button size="small" variant="outlined" disabled={busy || !!pending} onClick={() => onCommand(name, agent.paused ? 'resume' : 'pause')}>{agent.paused ? 'Resume' : 'Pause'}</Button>
        </>}
      </Box>
      {agent && <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{agent.summary || agent.description}</Typography>}
      {agent && <AgentSettings agent={agent} />}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      {commandError && <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissError}>{commandError}</Alert>}
      <TaskDialog agent={tasking ? agent : null} onClose={() => setTasking(false)} onSent={onNotice} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
        <RoundedPaper sx={{ p: 1, maxHeight: '75vh', overflow: 'auto' }}>
          {!runs && <CircularProgress size={20} sx={{ m: 2 }} />}
          {runs?.length === 0 && <Typography variant="body2" sx={{ p: 1, color: 'text.secondary' }}>No runs yet.</Typography>}
          {runs?.map(r => (
            <Box key={r.run_id} component={RouterLink} to={`/agents/${name}?run=${encodeURIComponent(r.run_id)}`} aria-current={r.run_id === current ? 'true' : undefined} sx={{ display: 'block', p: 1, borderRadius: 1, color: 'inherit', textDecoration: 'none', bgcolor: r.run_id === current ? '#EEF2FF' : undefined, '&:hover': { bgcolor: '#F8FAFC' } }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><StatusChip status={r.status} /><Typography variant="caption" sx={{ color: '#475569' }}>{fmtTime(r.started_at)} · {r.minutes ?? '—'} min</Typography></Box>
              <Typography variant="body2" sx={{ mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{runLine(r)}</Typography>
            </Box>
          ))}
        </RoundedPaper>
        <RoundedPaper sx={{ p: 2, minWidth: 0 }}>{current ? <RunView runId={current} /> : <Typography variant="body2" sx={{ color: 'text.secondary' }}>Pick a run.</Typography>}</RoundedPaper>
      </Box>
    </Box>
  );
}
